import {
  zoomTransform,
  event,
  select,
  format,
  timeFormat,
  line as lineD3,
  area as areaD3,
} from "d3";
import {
  colorDarkExpo,
  grayLightBackground,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import {
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  create2YTooltips,
  inRange,
  mouseDate,
  roundDate,
  zoomSetup,
} from "@/_core/charts/TechnicalAnalysis";
import { populateTableComparison } from "@/_core/data/Markets";

export function ComparisonCorrelHover(
  _this,
  isTwoAxes,
  isTwoSeries,
  colorTheme,
) {
  const auxName = "Correl";
  let valueLast = _this.chartDataRaw[_this.chartDataRaw.length - 1].macd;
  let xTooltipWidth = 80;
  let yTooltip = create2YTooltips(
    _this.svgNodeCorrel,
    _this.currencyFormat,
    valueLast,
    xTooltipWidth,
    colorDarkExpo(colorTheme),
    colorDarkExpo(colorTheme),
  );
  let y2Tooltip = yTooltip["y2Tooltip"];
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(_this.svgNodeCorrel, auxName, grayLightBackground(colorTheme), 1);
  vLineCreate(_this.svgNodeCorrel, auxName, grayLightBackground(colorTheme), 1);
  dotCreate(
    _this.svgNodeCorrel,
    auxName,
    3,
    textactive2(colorTheme),
    colorDarkExpo(colorTheme),
    3,
  );

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName)._groups[0][0],
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(
        event.layerX - _this.svgNodeCorrel.margin.left,
        _this.svgNodeCorrel.width,
      ),
      0,
    );
    var xt = _this.transform.rescaleX(_this.svgNodeCorrel.x);
    var yt = _this.svgNodeCorrel.y;
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= _this.chartDataRaw[_this.chartDataRaw.length - 1].date) {
      selData = mouseDate(xt.invert(xPixels), _this.chartDataRaw);
    } else {
      selData = JSON.parse(
        JSON.stringify(_this.chartDataRaw[_this.chartDataRaw.length - 1]),
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(
        event.layerY - _this.svgNodeCorrel.margin.top,
        _this.svgNodeCorrel.height,
      ),
      0,
    );
    let yRate = yt.invert(yPixels);
    yTooltip
      .style("opacity", 1)
      .style(
        "right",
        _this.svgNodeCorrel.width + _this.svgNodeCorrel.margin.right + 2 + "px",
      )
      .style("top", yPixels + _this.svgNodeCorrel.margin.top - 11 + "px")
      .html(format(",.2r")(yRate));
    y2Tooltip
      .style("opacity", 1)
      .style(
        "left",
        _this.svgNodeCorrel.width + _this.svgNodeCorrel.margin.left + 2 + "px",
      )
      .style("top", yPixels + _this.svgNodeCorrel.margin.top - 11 + "px")
      .html(format(",.2r")(yRate));
    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        xt(selData.date) +
          _this.svgNodeCorrel.margin.left -
          xTooltipWidth / 2 +
          "px",
      )
      .style(
        "top",
        _this.svgNodeCorrel.height + _this.svgNodeCorrel.margin.top + 2 + "px",
      )
      .html(timeFormat(_this.dateFormat)(selData.date));

    select(".hHoverLineCorrel")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let xPix = xt(selData.date);

    select(".vHoverLineTA")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    select(".vHoverLineSpread")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    select(".vHoverLineCorrel")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    if (highlightCandles) {
      const yt2 = _this.svgNode.y;
      select(".dotTA")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt2(selData[_this.fieldUp])})`,
        );
      if (isTwoSeries) {
        const yt22 = isTwoAxes ? _this.svgNode.y2 : _this.svgNode.y;
        select(".dotTA2")
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt22(selData[_this.fieldUp2])})`,
          );
      }
      const yt3 = _this.svgNodeSpread.y;
      select(".dotSpread")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt3(
            selData[_this.spreadFieldName],
          )})`,
        );
      select(".dotCorrel")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(selData["corr"])})`,
        );
    } else {
      select(".dotTA").style("opacity", 0);
      if (isTwoSeries) {
        select(".dotTA2").style("opacity", 0);
      }
      select(".dotSpread").style("opacity", 0);
      select(".dotCorrel").style("opacity", 0);
    }

    populateTableComparison(
      {
        date: selData.date,
        main: selData[_this.fieldUp],
        second: selData[_this.fieldUp2],
        spread: selData[_this.spreadFieldName],
        corr: selData.corr,
      },
      format(_this.currencyFormat),
      timeFormat("%d %b %y"),
    );
  }
  function mouseout() {
    yTooltip
      .style("right", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    y2Tooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".vHoverLineTA").style("opacity", 0);
    select(".vHoverLineSpread").style("opacity", 0);
    select(".hHoverLineCorrel").style("opacity", 0);
    select(".vHoverLineCorrel").style("opacity", 0);
    select(".dotTA").style("opacity", 0);
    if (isTwoSeries) {
      select(".dotTA2").style("opacity", 0);
    }
    select(".dotSpread").style("opacity", 0);
    select(".dotCorrel").style("opacity", 0);

    let d = _this.chartDataRaw[_this.chartDataRaw.length - 1];
    populateTableComparison(
      {
        date: d.date,
        main: d[_this.fieldUp],
        second: d[_this.fieldUp2],
        spread: d[_this.spreadFieldName],
        corr: d.corr,
      },
      format(_this.currencyFormat),
      timeFormat("%d %b %y"),
    );
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    yTooltip
      .style("right", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    y2Tooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".vHoverLineTA").style("opacity", 0);
    select(".vHoverLineSpread").style("opacity", 0);
    select(".hHoverLineCorrel").style("opacity", 0);
    select(".vHoverLineCorrel").style("opacity", 0);
    select(".dotTA").style("opacity", 0);
    if (isTwoSeries) {
      select(".dotTA2").style("opacity", 0);
    }
    select(".dotSpread").style("opacity", 0);
    select(".dotCorrel").style("opacity", 0);

    _this.transform = event.transform;
    _this.svgNodeCorrel.gX.call(
      _this.svgNodeCorrel.xAxis.scale(
        _this.transform.rescaleX(_this.svgNodeCorrel.x),
      ),
    );
    const xt = _this.transform.rescaleX(_this.svgNodeCorrel.x);
    CorrelZooming(_this.transform, xt, _this);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      select(".hoverParentTA").call(_this.zoomTA.transform, _this.transform);
      select(".hoverParentSpread").call(
        _this.zoomSpread.transform,
        _this.transform,
      );
      _this.externalZoom = false;
    }
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNodeCorrel.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var xmin = new Date(xt.invert(0));
      var xmax = new Date(xt.invert(_this.svgNodeCorrel.width));

      if (!_this.externalZoom) {
        let data = _this.chartDataRaw.map((d) => d);
        _this.filteredData = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }
      let yt = _this.svgNodeCorrel.y;

      CorrelZoomEnd(_this.transform, xt, yt, _this.filteredData, _this);
    }, 200);
  }

  var resizeTimer;
  zoomSetup(
    _this,
    "svgNodeCorrel",
    "zoom" + auxName,
    auxName,
    "",
    mouseover,
    mouseout,
    zoomed,
    zoomEnd,
  );
}

export function CorrelZooming(t, xt, _this) {
  let area, line;
  line = lineD3()
    .defined((d) => !Number.isNaN(d["corr"]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNodeCorrel.y(d["corr"]));
  _this.svgNodeCorrel["upperLineCorrel"].attr("d", (d) => line(d));
  line = lineD3()
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNodeCorrel.y(d["zero"]));
  _this.svgNodeCorrel["lowerLineCorrel"].attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d["corr"]))
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y0((d) => _this.svgNodeCorrel.y(d["corr"]))
    .y1((d) => _this.svgNodeCorrel.y(d["zero"]));
  _this.svgNodeCorrel["areaCorrel"].attr("d", area);
}

export function CorrelZoomEnd(t, xt, yt, filtered, _this) {
  let area, line;
  line = lineD3()
    .defined((d) => !Number.isNaN(d["corr"]))
    .x((d) => xt(d.date))
    .y((d) => yt(d["corr"]));
  _this.svgNodeCorrel["upperLineCorrel"]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  line = lineD3()
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y((d) => yt(d["zero"]));
  _this.svgNodeCorrel["lowerLineCorrel"]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d["corr"]))
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y0((d) => yt(d["corr"]))
    .y1((d) => yt(d["zero"]));
  _this.svgNodeCorrel["areaCorrel"]
    .data([filtered])
    .join("path")
    .attr("d", area);
}
